import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Form,
  FormFeedback,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateAccountVendor,
  useEditAccountVendor,
} from "hooks/useAccountVendor"

const AccountVendorAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountVendor()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountVendor()

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter vendor name"),
    phone: Yup.string().nullable().max(50, "Phone is too long"),
    email: Yup.string().nullable().email("Please enter a valid email"),
    address: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        phone: values.phone?.trim() || null,
        email: values.email?.trim() || null,
        address: values.address?.trim() || null,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Vendor ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        toast.error("Failed to save vendor.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      phone: editedData?.phone || "",
      email: editedData?.email || "",
      address: editedData?.address || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Vendor"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Vendor" : "Add Vendor"}
      size="lg"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Basic Information</h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="name">Vendor Name</Label>
                  <InputField
                    type="text"
                    name="name"
                    id="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="phone">Phone</Label>
                  <InputField
                    type="text"
                    name="phone"
                    id="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    invalid={formik.touched.phone && !!formik.errors.phone}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <FormFeedback type="invalid">{formik.errors.phone}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Contact Details</h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="email">Email</Label>
                  <InputField
                    type="email"
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    invalid={formik.touched.email && !!formik.errors.email}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="address">Address</Label>
                  <InputField
                    type="textarea"
                    name="address"
                    id="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    invalid={formik.touched.address && !!formik.errors.address}
                  />
                  {formik.touched.address && formik.errors.address && (
                    <FormFeedback type="invalid">{formik.errors.address}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountVendorAddEdit
